/**
 * ErrorBoundary Component — aicc-app
 *
 * 기능:
 * - 런타임 에러 캐치 및 폴백 UI 표시
 * - 에러 ID 생성 (지원팀 신고 참조용)
 * - logError 추상화 슬롯 (추후 Sentry 연동 가능)
 * - 라우트 전환 시 에러 상태 자동 초기화 (ErrorBoundaryWithReset 래퍼)
 * - 복구 시 React Query 캐시 초기화
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { ShieldCheck, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { queryClient } from "@/main";

// ─────────── 에러 로깅 추상화 슬롯 ───────────
// 추후 Sentry 연동 시: window.__reportError = (error, info, errorId) => Sentry.captureException(error, { extra: { ...info, errorId } })
declare global {
  interface Window {
    __reportError?: (
      error: Error,
      errorInfo: ErrorInfo,
      errorId: string,
    ) => void;
  }
}

function logError(error: Error, errorInfo: ErrorInfo, errorId: string): void {
  console.error(`[ErrorBoundary] id=${errorId}`, error, errorInfo);
  if (typeof window.__reportError === "function") {
    window.__reportError(error, errorInfo, errorId);
  }
}

function generateErrorId(): string {
  return `ERR-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

// ─────────── Props / State ───────────
interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

// ─────────── Core ErrorBoundary (class component) ───────────
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    errorId: null,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: generateErrorId(),
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorId = this.state.errorId ?? generateErrorId();
    logError(error, errorInfo, errorId);
    this.setState({ errorInfo, errorId });
  }

  private handleReload = () => {
    queryClient.clear();
    window.location.reload();
  };

  private handleGoHome = () => {
    queryClient.clear();
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
    window.location.href = "/dashboard";
  };

  public reset() {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorId } = this.state;

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-background via-background to-muted/30 px-4">
          <div className="text-center max-w-lg animate-in fade-in-50 slide-in-from-bottom-4 duration-700">
            {/* 아이콘 — bg-primary/10 원형 컨테이너 */}
            <div className="flex items-center justify-center mb-6">
              <div className="rounded-full bg-primary/10 p-5">
                <ShieldCheck className="h-16 w-16 text-primary" />
              </div>
            </div>

            {/* 제목 */}
            <h1 className="text-2xl font-bold text-foreground mb-3">
              일시적인 문제가 발생했습니다
            </h1>

            {/* 본문 */}
            <p className="text-muted-foreground mb-4 leading-relaxed">
              걱정 마세요! 데이터는 안전합니다.
              <br />
              잠시 후 다시 시도해 주세요.
            </p>

            {/* 에러 ID — 지원팀 신고 시 참조 */}
            {errorId && (
              <p className="text-xs text-muted-foreground/70 mb-6 font-mono">
                오류 코드: {errorId}
              </p>
            )}

            {/* DEV 환경 에러 상세 */}
            {import.meta.env.DEV && error && (
              <div className="mb-6 p-4 bg-muted/50 rounded-lg text-left">
                <p className="text-sm font-mono text-foreground break-all">
                  {error.toString()}
                </p>
                {this.state.errorInfo && (
                  <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-40">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            {/* 액션 버튼 */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-6">
              <Button onClick={this.handleReload} className="gap-2">
                <RefreshCw className="h-4 w-4" />
                새로고침
              </Button>
              <Button
                variant="outline"
                onClick={this.handleGoHome}
                className="gap-2"
              >
                <Home className="h-4 w-4" />
                홈으로 돌아가기
              </Button>
            </div>

            {/* 1:1 문의 링크 — <a> 사용 (ErrorBoundary는 Router 외부에서도 쓰일 수 있음) */}
            <p className="text-xs text-muted-foreground/70">
              문제가 계속되면{" "}
              <a
                href="/inquiries"
                className="underline underline-offset-2 hover:text-foreground transition-colors"
              >
                1:1 문의
              </a>
              를 남겨주세요
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─────────── ErrorBoundaryWithReset (라우트 전환 시 자동 리셋) ───────────
/**
 * useLocation의 pathname을 key로 사용하여
 * 라우트 전환 시 ErrorBoundary를 remount → 에러 상태 자동 초기화
 */
export function ErrorBoundaryWithReset({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
